import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { usePreferences } from '../contexts/PreferencesContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

// ── Type Definitions ──────────────────────────────────────────

type SponsorshipTier = 'Platinum' | 'Gold' | 'Silver' | 'Bronze' | 'Custom';
type SponsorStatus = 'Active' | 'Inactive';
type PaymentStatusFilter = 'Unpaid' | 'PartiallyPaid' | 'FullyPaid';
type PaymentMethod = 'Cash' | 'Check' | 'CreditCard' | 'BankTransfer' | 'PayPal' | 'Zelle' | 'Other';

interface Sponsor {
  id: string;
  sponsorName: string;
  contactPerson: string;
  email: string;
  phone: string | null;
  mailingAddress: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  tier: SponsorshipTier;
  status: SponsorStatus;
  pledgedAmount: number;
  receivedAmount: number;
  createdAt: string;
  updatedAt: string;
}

interface SponsorDetail extends Sponsor {
  payments: SponsorPayment[];
  events: SponsorEventAssociation[];
}

interface SponsorPayment {
  id: string;
  sponsorId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  createdAt: string;
}

interface SponsorEventAssociation {
  id: string;
  sponsorId: string;
  eventId: string;
  associationDate: string;
  sponsorAmount: number | null;
  event: { id: string; name: string; date: string };
}

interface RevenueSummary {
  totalPledged: number;
  totalReceived: number;
  progressPercent: number;
}

interface CreateSponsorPayload {
  sponsorName: string;
  contactPerson: string;
  email: string;
  tier: SponsorshipTier;
  phone?: string;
  mailingAddress?: string;
  websiteUrl?: string;
  logoUrl?: string;
  pledgedAmount?: number;
}

interface CreatePaymentPayload {
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
}

interface AssociateEventPayload {
  eventId: string;
  sponsorAmount?: number;
}

interface SponsorFilters {
  tier?: SponsorshipTier;
  status?: SponsorStatus;
  paymentStatus?: PaymentStatusFilter;
}

interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

interface EventOption {
  id: string;
  name: string;
  date: string;
}

// ── Constants ─────────────────────────────────────────────────

const SPONSORSHIP_TIERS: SponsorshipTier[] = ['Platinum', 'Gold', 'Silver', 'Bronze', 'Custom'];
const SPONSOR_STATUSES: SponsorStatus[] = ['Active', 'Inactive'];
const PAYMENT_STATUS_OPTIONS: PaymentStatusFilter[] = ['Unpaid', 'PartiallyPaid', 'FullyPaid'];
const PAYMENT_METHODS: PaymentMethod[] = ['Cash', 'Check', 'CreditCard', 'BankTransfer', 'PayPal', 'Zelle', 'Other'];

// ── Utility Functions ─────────────────────────────────────────

export function computeRevenueSummary(sponsors: Sponsor[]): RevenueSummary {
  const activeSponsors = sponsors.filter(s => s.status === 'Active');
  const totalPledged = activeSponsors.reduce((sum, s) => sum + Number(s.pledgedAmount), 0);
  const totalReceived = activeSponsors.reduce((sum, s) => sum + Number(s.receivedAmount), 0);
  const progressPercent = totalPledged > 0 ? Math.min((totalReceived / totalPledged) * 100, 100) : 0;
  return { totalPledged, totalReceived, progressPercent };
}

export function computeProgressPercent(pledgedAmount: number, receivedAmount: number): number {
  if (pledgedAmount <= 0) return 0;
  if (receivedAmount >= pledgedAmount) return 100;
  return (receivedAmount / pledgedAmount) * 100;
}

export function buildFilterQuery(filters: SponsorFilters): string {
  const params = new URLSearchParams();
  if (filters.tier) params.set('tier', filters.tier);
  if (filters.status) params.set('status', filters.status);
  if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
  return params.toString() ? `?${params.toString()}` : '';
}

export function validateOnboardingForm(data: Partial<CreateSponsorPayload>): ValidationResult {
  const errors: Record<string, string> = {};
  if (!data.sponsorName?.trim()) errors.sponsorName = 'Sponsor name is required';
  if (!data.contactPerson?.trim()) errors.contactPerson = 'Contact person is required';
  if (!data.email?.trim()) errors.email = 'Email is required';
  if (!data.tier) errors.tier = 'Tier is required';
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validatePaymentForm(data: Partial<CreatePaymentPayload>): ValidationResult {
  const errors: Record<string, string> = {};
  if (!data.amount || data.amount <= 0) errors.amount = 'Amount is required and must be positive';
  if (!data.paymentDate) errors.paymentDate = 'Payment date is required';
  if (!data.paymentMethod) errors.paymentMethod = 'Payment method is required';
  return { valid: Object.keys(errors).length === 0, errors };
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem', cursor: 'pointer' },
  fieldError: { color: '#d32f2f', fontSize: '0.75rem', marginTop: '0.25rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  trClickable: { cursor: 'pointer', transition: 'background-color 0.15s' },
  trExpanded: { backgroundColor: '#f0f7f2' },
  card: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa', cursor: 'pointer', transition: 'background-color 0.15s' },
  cardExpanded: { backgroundColor: '#f0f7f2', borderColor: '#1a6b3c' },
  detailPanel: { padding: '1.5rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginTop: '0.5rem', border: '1px solid #e0e0e0' },
  revenueCard: { padding: '1.5rem', backgroundColor: '#0f2d1f', borderRadius: '8px', color: '#fff', marginBottom: '1.5rem' },
  revenueAmount: { fontSize: '2rem', fontWeight: 700, color: '#c9a84c' },
  revenueLabel: { fontSize: '0.85rem', color: '#a3b8a9', marginTop: '0.25rem' },
  progressBarContainer: { background: '#e0e0e0', borderRadius: '4px', height: '8px', width: '100%', marginTop: '0.5rem' },
  progressBarFill: { borderRadius: '4px', height: '100%', transition: 'width 0.3s' },
};

// ── Component ─────────────────────────────────────────────────

export function SponsorsPage() {
  const { canEdit } = usePageAuthorization('sponsors');
  const { preferences } = usePreferences();

  // Data state
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary>({ totalPledged: 0, totalReceived: 0, progressPercent: 0 });
  const [selectedDetail, setSelectedDetail] = useState<SponsorDetail | null>(null);
  const [events, setEvents] = useState<EventOption[]>([]);

  // UI state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() =>
    preferences.defaultViewMode === 'table' ? 'table' : 'cards'
  );
  const [expandedSponsorId, setExpandedSponsorId] = useState<string | null>(null);
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [filters, setFilters] = useState<SponsorFilters>({});
  const [editMode, setEditMode] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);

  // Error/loading
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Onboarding form fields
  const [formSponsorName, setFormSponsorName] = useState('');
  const [formContactPerson, setFormContactPerson] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMailingAddress, setFormMailingAddress] = useState('');
  const [formWebsiteUrl, setFormWebsiteUrl] = useState('');
  const [formLogoUrl, setFormLogoUrl] = useState('');
  const [formTier, setFormTier] = useState<SponsorshipTier | ''>('');
  const [formPledgedAmount, setFormPledgedAmount] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Edit fields
  const [editSponsorName, setEditSponsorName] = useState('');
  const [editContactPerson, setEditContactPerson] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editMailingAddress, setEditMailingAddress] = useState('');
  const [editWebsiteUrl, setEditWebsiteUrl] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState('');
  const [editTier, setEditTier] = useState<SponsorshipTier>('Bronze');
  const [editStatus, setEditStatus] = useState<SponsorStatus>('Active');
  const [editPledgedAmount, setEditPledgedAmount] = useState('');

  // Payment form fields
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('Cash');
  const [payErrors, setPayErrors] = useState<Record<string, string>>({});

  // Event association form fields
  const [assocEventId, setAssocEventId] = useState('');
  const [assocAmount, setAssocAmount] = useState('');

  // ── Data Fetching ─────────────────────────────────────────────

  async function fetchSponsors() {
    setLoading(true);
    try {
      const query = buildFilterQuery(filters);
      const data = await apiClient<Sponsor[]>(`/sponsors${query}`);
      setSponsors(data);
      setRevenueSummary(computeRevenueSummary(data));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load sponsors.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSponsorDetail(sponsorId: string) {
    try {
      const data = await apiClient<SponsorDetail>(`/sponsors/${sponsorId}`);
      setSelectedDetail(data);
      // Populate edit fields
      setEditSponsorName(data.sponsorName);
      setEditContactPerson(data.contactPerson);
      setEditEmail(data.email);
      setEditPhone(data.phone || '');
      setEditMailingAddress(data.mailingAddress || '');
      setEditWebsiteUrl(data.websiteUrl || '');
      setEditLogoUrl(data.logoUrl || '');
      setEditTier(data.tier);
      setEditStatus(data.status);
      setEditPledgedAmount(String(data.pledgedAmount || ''));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load sponsor details.');
    }
  }

  async function fetchEvents() {
    try {
      const data = await apiClient<EventOption[]>('/events');
      setEvents(data);
    } catch {
      // silently fail - events dropdown will be empty
    }
  }

  useEffect(() => {
    fetchSponsors();
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchSponsors();
  }, [filters]);

  // ── Sponsor Actions ────────────────────────────────────────────

  async function handleCreateSponsor(e: FormEvent) {
    e.preventDefault();
    const payload: Partial<CreateSponsorPayload> = {
      sponsorName: formSponsorName,
      contactPerson: formContactPerson,
      email: formEmail,
      tier: formTier as SponsorshipTier || undefined,
    };
    const validation = validateOnboardingForm(payload);
    if (!validation.valid) {
      setFormErrors(validation.errors);
      return;
    }
    try {
      const body: CreateSponsorPayload = {
        sponsorName: formSponsorName.trim(),
        contactPerson: formContactPerson.trim(),
        email: formEmail.trim(),
        tier: formTier as SponsorshipTier,
        ...(formPhone.trim() && { phone: formPhone.trim() }),
        ...(formMailingAddress.trim() && { mailingAddress: formMailingAddress.trim() }),
        ...(formWebsiteUrl.trim() && { websiteUrl: formWebsiteUrl.trim() }),
        ...(formLogoUrl.trim() && { logoUrl: formLogoUrl.trim() }),
        ...(formPledgedAmount && { pledgedAmount: parseFloat(formPledgedAmount) }),
      };
      await apiClient('/sponsors', { method: 'POST', body });
      // Reset form
      setFormSponsorName('');
      setFormContactPerson('');
      setFormEmail('');
      setFormPhone('');
      setFormMailingAddress('');
      setFormWebsiteUrl('');
      setFormLogoUrl('');
      setFormTier('');
      setFormPledgedAmount('');
      setFormErrors({});
      setShowOnboardingForm(false);
      setError('');
      fetchSponsors();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setFormErrors({ sponsorName: 'Sponsor name already exists' });
      } else {
        setError(apiErr.message || 'Failed to create sponsor.');
      }
    }
  }

  async function handleSaveSponsor() {
    if (!selectedDetail) return;
    try {
      await apiClient(`/sponsors/${selectedDetail.id}`, {
        method: 'PATCH',
        body: {
          sponsorName: editSponsorName.trim(),
          contactPerson: editContactPerson.trim(),
          email: editEmail.trim(),
          phone: editPhone.trim() || null,
          mailingAddress: editMailingAddress.trim() || null,
          websiteUrl: editWebsiteUrl.trim() || null,
          logoUrl: editLogoUrl.trim() || null,
          tier: editTier,
          status: editStatus,
          pledgedAmount: editPledgedAmount ? parseFloat(editPledgedAmount) : 0,
        },
      });
      setEditMode(false);
      setError('');
      fetchSponsors();
      fetchSponsorDetail(selectedDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('Sponsor name already exists');
      } else {
        setError(apiErr.message || 'Failed to update sponsor.');
      }
    }
  }

  async function handleDeleteSponsor() {
    if (!selectedDetail) return;
    if (!confirm(`Are you sure you want to delete "${selectedDetail.sponsorName}"?`)) return;
    try {
      await apiClient(`/sponsors/${selectedDetail.id}`, { method: 'DELETE' });
      setExpandedSponsorId(null);
      setSelectedDetail(null);
      setEditMode(false);
      setError('');
      fetchSponsors();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete sponsor.');
    }
  }

  // ── Payment Actions ───────────────────────────────────────────

  async function handleAddPayment(e: FormEvent) {
    e.preventDefault();
    if (!selectedDetail) return;
    const payload: Partial<CreatePaymentPayload> = {
      amount: payAmount ? parseFloat(payAmount) : undefined,
      paymentDate: payDate || undefined,
      paymentMethod: payMethod || undefined,
    };
    const validation = validatePaymentForm(payload);
    if (!validation.valid) {
      setPayErrors(validation.errors);
      return;
    }
    try {
      await apiClient(`/sponsors/${selectedDetail.id}/payments`, {
        method: 'POST',
        body: {
          amount: parseFloat(payAmount),
          paymentDate: payDate,
          paymentMethod: payMethod,
        },
      });
      setPayAmount('');
      setPayDate('');
      setPayMethod('Cash');
      setPayErrors({});
      setShowPaymentForm(false);
      setError('');
      fetchSponsorDetail(selectedDetail.id);
      fetchSponsors();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to record payment.');
    }
  }

  // ── Event Association Actions ─────────────────────────────────

  async function handleAssociateEvent(e: FormEvent) {
    e.preventDefault();
    if (!selectedDetail || !assocEventId) return;
    try {
      const body: AssociateEventPayload = {
        eventId: assocEventId,
        ...(assocAmount && { sponsorAmount: parseFloat(assocAmount) }),
      };
      await apiClient(`/sponsors/${selectedDetail.id}/events`, {
        method: 'POST',
        body,
      });
      setAssocEventId('');
      setAssocAmount('');
      setShowEventForm(false);
      setError('');
      fetchSponsorDetail(selectedDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('Association already exists');
      } else {
        setError(apiErr.message || 'Failed to associate event.');
      }
    }
  }

  async function handleRemoveEventAssociation(eventId: string) {
    if (!selectedDetail) return;
    if (!confirm('Remove this event association?')) return;
    try {
      await apiClient(`/sponsors/${selectedDetail.id}/events/${eventId}`, { method: 'DELETE' });
      setError('');
      fetchSponsorDetail(selectedDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to remove event association.');
    }
  }

  // ── Expansion Handler ─────────────────────────────────────────

  function handleSponsorClick(sponsorId: string) {
    if (expandedSponsorId === sponsorId) {
      setExpandedSponsorId(null);
      setSelectedDetail(null);
      setEditMode(false);
      setShowPaymentForm(false);
      setShowEventForm(false);
      return;
    }
    setExpandedSponsorId(sponsorId);
    setEditMode(false);
    setShowPaymentForm(false);
    setShowEventForm(false);
    fetchSponsorDetail(sponsorId);
  }

  // ── Helpers ───────────────────────────────────────────────────

  function formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  }

  // Sort sponsors alphabetically by sponsorName (case-insensitive)
  const sortedSponsors = [...sponsors].sort((a, b) =>
    a.sponsorName.toLowerCase().localeCompare(b.sponsorName.toLowerCase())
  );

  function renderProgressBar(pledged: number, received: number, height?: string) {
    const percent = computeProgressPercent(pledged, received);
    const color = received >= pledged && pledged > 0 ? '#4caf50' : '#c9a84c';
    return (
      <div style={{ ...styles.progressBarContainer, height: height || '8px' }}>
        <div style={{ ...styles.progressBarFill, width: `${percent}%`, background: color, height: height || '8px' }} />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Sponsor Management</h1>

      {/* Error Banner */}
      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {/* Revenue Summary Card */}
      <div style={styles.revenueCard}>
        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={styles.revenueAmount}>{formatCurrency(revenueSummary.totalPledged)}</div>
            <div style={styles.revenueLabel}>Total Pledged</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={styles.revenueAmount}>{formatCurrency(revenueSummary.totalReceived)}</div>
            <div style={styles.revenueLabel}>Total Received</div>
          </div>
        </div>
        <div style={{ padding: '0 2rem' }}>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '4px', height: '10px', width: '100%' }}>
            <div style={{ background: '#c9a84c', borderRadius: '4px', height: '100%', width: `${revenueSummary.progressPercent}%`, transition: 'width 0.3s' }} />
          </div>
          <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.8rem', color: '#a3b8a9' }}>
            {revenueSummary.progressPercent.toFixed(1)}% collected
          </div>
        </div>
      </div>

      {/* Sponsor Directory Section */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Sponsor Directory</h2>
            <button
              style={viewMode === 'cards' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {/* Filter Bar */}
            <select
              style={styles.select}
              value={filters.tier || ''}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value as SponsorshipTier || undefined })}
            >
              <option value="">All Tiers</option>
              {SPONSORSHIP_TIERS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              style={styles.select}
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as SponsorStatus || undefined })}
            >
              <option value="">All Statuses</option>
              {SPONSOR_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              style={styles.select}
              value={filters.paymentStatus || ''}
              onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value as PaymentStatusFilter || undefined })}
            >
              <option value="">All Payment Status</option>
              {PAYMENT_STATUS_OPTIONS.map((ps) => (
                <option key={ps} value={ps}>{ps}</option>
              ))}
            </select>
            {/* Add Sponsor Button */}
            {canEdit && (
            <button style={styles.btn} onClick={() => setShowOnboardingForm(!showOnboardingForm)}>
              + Add Sponsor
            </button>
            )}
          </div>
        </div>

        {/* Onboarding Form */}
        {showOnboardingForm && (
          <form onSubmit={handleCreateSponsor} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>New Sponsor</h3>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>Sponsor Name *</label>
                <input style={{ ...styles.input, width: '100%' }} value={formSponsorName} onChange={(e) => setFormSponsorName(e.target.value)} />
                {formErrors.sponsorName && <div style={styles.fieldError}>{formErrors.sponsorName}</div>}
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Contact Person *</label>
                <input style={{ ...styles.input, width: '100%' }} value={formContactPerson} onChange={(e) => setFormContactPerson(e.target.value)} />
                {formErrors.contactPerson && <div style={styles.fieldError}>{formErrors.contactPerson}</div>}
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input type="email" style={{ ...styles.input, width: '100%' }} value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                {formErrors.email && <div style={styles.fieldError}>{formErrors.email}</div>}
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Tier *</label>
                <select style={{ ...styles.select, width: '100%' }} value={formTier} onChange={(e) => setFormTier(e.target.value as SponsorshipTier)}>
                  <option value="">Select tier...</option>
                  {SPONSORSHIP_TIERS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {formErrors.tier && <div style={styles.fieldError}>{formErrors.tier}</div>}
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Phone</label>
                <input style={{ ...styles.input, width: '100%' }} value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Mailing Address</label>
                <input style={{ ...styles.input, width: '100%' }} value={formMailingAddress} onChange={(e) => setFormMailingAddress(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Website URL</label>
                <input style={{ ...styles.input, width: '100%' }} value={formWebsiteUrl} onChange={(e) => setFormWebsiteUrl(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Logo URL</label>
                <input style={{ ...styles.input, width: '100%' }} value={formLogoUrl} onChange={(e) => setFormLogoUrl(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Pledged Amount</label>
                <input type="number" step="0.01" style={{ ...styles.input, width: '100%' }} value={formPledgedAmount} onChange={(e) => setFormPledgedAmount(e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <button type="submit" style={styles.btn}>Create Sponsor</button>
              <button type="button" style={styles.btnGold} onClick={() => { setShowOnboardingForm(false); setFormErrors({}); }}>Cancel</button>
            </div>
          </form>
        )}

        {/* Sponsor List */}
        {loading && <p style={{ color: '#666' }}>Loading sponsors...</p>}
        {!loading && sortedSponsors.length === 0 ? (
          <p style={{ color: '#666' }}>No sponsors match the current filters.</p>
        ) : viewMode === 'cards' ? (
          /* Card View */
          sortedSponsors.map((sponsor) => (
            <React.Fragment key={sponsor.id}>
              <div
                style={{
                  ...styles.card,
                  ...(expandedSponsorId === sponsor.id ? styles.cardExpanded : {}),
                }}
                onClick={() => handleSponsorClick(sponsor.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <strong style={{ color: '#0f2d1f', fontSize: '1rem' }}>{sponsor.sponsorName}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#444', marginTop: '0.25rem' }}>
                      {sponsor.tier} · {sponsor.status}
                    </div>
                  </div>
                  <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff', backgroundColor: sponsor.status === 'Active' ? '#1a6b3c' : '#999' }}>
                    {sponsor.status}
                  </span>
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                    {formatCurrency(sponsor.receivedAmount)} / {formatCurrency(sponsor.pledgedAmount)}
                  </div>
                  {renderProgressBar(sponsor.pledgedAmount, sponsor.receivedAmount)}
                </div>
              </div>
              {/* Detail Panel (Card View) */}
              {expandedSponsorId === sponsor.id && renderDetailPanel()}
            </React.Fragment>
          ))
        ) : (
          /* Table View */
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Sponsor Name</th>
                  <th style={styles.th}>Contact Person</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Pledged</th>
                  <th style={styles.th}>Received</th>
                  <th style={styles.th}>Progress</th>
                </tr>
              </thead>
              <tbody>
                {sortedSponsors.map((sponsor) => (
                  <React.Fragment key={sponsor.id}>
                    <tr
                      style={{
                        ...styles.trClickable,
                        ...(expandedSponsorId === sponsor.id ? styles.trExpanded : {}),
                      }}
                      onClick={() => handleSponsorClick(sponsor.id)}
                    >
                      <td style={styles.td}><strong style={{ color: '#0f2d1f' }}>{sponsor.sponsorName}</strong></td>
                      <td style={styles.td}>{sponsor.contactPerson}</td>
                      <td style={styles.td}>{sponsor.tier}</td>
                      <td style={styles.td}>{sponsor.status}</td>
                      <td style={styles.td}>{formatCurrency(sponsor.pledgedAmount)}</td>
                      <td style={styles.td}>{formatCurrency(sponsor.receivedAmount)}</td>
                      <td style={{ ...styles.td, minWidth: '100px' }}>{renderProgressBar(sponsor.pledgedAmount, sponsor.receivedAmount)}</td>
                    </tr>
                    {/* Detail Panel (Table View) */}
                    {expandedSponsorId === sponsor.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0.5rem' }}>
                          {renderDetailPanel()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ── Detail Panel Renderer ─────────────────────────────────────

  function renderDetailPanel() {
    if (!selectedDetail) {
      return <div style={styles.detailPanel}><p style={{ color: '#666' }}>Loading sponsor details...</p></div>;
    }

    return (
      <div style={styles.detailPanel} onClick={(e) => e.stopPropagation()}>
        {/* Edit/View Toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#0f2d1f', fontSize: '1rem' }}>
            {editMode ? 'Edit Sponsor' : 'Sponsor Details'}
          </h3>
          <div>
            {canEdit && !editMode && (
              <button style={styles.btn} onClick={() => setEditMode(true)}>Edit</button>
            )}
            {canEdit && editMode && (
              <>
                <button style={styles.btn} onClick={handleSaveSponsor}>Save</button>
                <button style={styles.btnGold} onClick={() => setEditMode(false)}>Cancel</button>
              </>
            )}
            {canEdit && <button style={styles.btnDanger} onClick={handleDeleteSponsor}>Delete</button>}
          </div>
        </div>

        {/* Sponsor Fields */}
        {editMode ? (
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Sponsor Name</label>
              <input style={{ ...styles.input, width: '100%' }} value={editSponsorName} onChange={(e) => setEditSponsorName(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Contact Person</label>
              <input style={{ ...styles.input, width: '100%' }} value={editContactPerson} onChange={(e) => setEditContactPerson(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" style={{ ...styles.input, width: '100%' }} value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone</label>
              <input style={{ ...styles.input, width: '100%' }} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mailing Address</label>
              <input style={{ ...styles.input, width: '100%' }} value={editMailingAddress} onChange={(e) => setEditMailingAddress(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Website URL</label>
              <input style={{ ...styles.input, width: '100%' }} value={editWebsiteUrl} onChange={(e) => setEditWebsiteUrl(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Logo URL</label>
              <input style={{ ...styles.input, width: '100%' }} value={editLogoUrl} onChange={(e) => setEditLogoUrl(e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Tier</label>
              <select style={{ ...styles.select, width: '100%' }} value={editTier} onChange={(e) => setEditTier(e.target.value as SponsorshipTier)}>
                {SPONSORSHIP_TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Status</label>
              <select style={{ ...styles.select, width: '100%' }} value={editStatus} onChange={(e) => setEditStatus(e.target.value as SponsorStatus)}>
                {SPONSOR_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Pledged Amount</label>
              <input type="number" step="0.01" style={{ ...styles.input, width: '100%' }} value={editPledgedAmount} onChange={(e) => setEditPledgedAmount(e.target.value)} />
            </div>
          </div>
        ) : (
          <div style={styles.formGrid}>
            <div style={styles.field}><label style={styles.label}>Sponsor Name</label><div>{selectedDetail.sponsorName}</div></div>
            <div style={styles.field}><label style={styles.label}>Contact Person</label><div>{selectedDetail.contactPerson}</div></div>
            <div style={styles.field}><label style={styles.label}>Email</label><div>{selectedDetail.email}</div></div>
            <div style={styles.field}><label style={styles.label}>Phone</label><div>{selectedDetail.phone || '—'}</div></div>
            <div style={styles.field}><label style={styles.label}>Mailing Address</label><div>{selectedDetail.mailingAddress || '—'}</div></div>
            <div style={styles.field}><label style={styles.label}>Website URL</label><div>{selectedDetail.websiteUrl || '—'}</div></div>
            <div style={styles.field}><label style={styles.label}>Logo URL</label><div>{selectedDetail.logoUrl || '—'}</div></div>
            <div style={styles.field}><label style={styles.label}>Tier</label><div>{selectedDetail.tier}</div></div>
            <div style={styles.field}><label style={styles.label}>Status</label><div>{selectedDetail.status}</div></div>
            <div style={styles.field}><label style={styles.label}>Pledged Amount</label><div>{formatCurrency(selectedDetail.pledgedAmount)}</div></div>
            <div style={styles.field}><label style={styles.label}>Received Amount</label><div>{formatCurrency(selectedDetail.receivedAmount)}</div></div>
          </div>
        )}

        {/* Payment History */}
        <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.95rem' }}>Payment History</h4>
            {canEdit && (
            <button
              style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
              onClick={() => setShowPaymentForm(!showPaymentForm)}
            >
              + Add Payment
            </button>
            )}
          </div>

          {/* Payment Form */}
          {showPaymentForm && (
            <form onSubmit={handleAddPayment} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Amount *</label>
                  <input type="number" step="0.01" style={styles.input} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                  {payErrors.amount && <div style={styles.fieldError}>{payErrors.amount}</div>}
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Date *</label>
                  <input type="date" style={styles.input} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                  {payErrors.paymentDate && <div style={styles.fieldError}>{payErrors.paymentDate}</div>}
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Method *</label>
                  <select style={styles.select} value={payMethod} onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  {payErrors.paymentMethod && <div style={styles.fieldError}>{payErrors.paymentMethod}</div>}
                </div>
                <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Record</button>
              </div>
            </form>
          )}

          {/* Payment List (reverse chronological) */}
          {selectedDetail.payments && selectedDetail.payments.length > 0 ? (
            <table style={{ ...styles.table, fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Method</th>
                </tr>
              </thead>
              <tbody>
                {[...selectedDetail.payments]
                  .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())
                  .map((payment) => (
                    <tr key={payment.id}>
                      <td style={styles.td}>{formatCurrency(payment.amount)}</td>
                      <td style={styles.td}>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                      <td style={styles.td}>{payment.paymentMethod}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>No payments recorded.</p>
          )}
        </div>

        {/* Event Associations */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.95rem' }}>Event Associations</h4>
            {canEdit && (
            <button
              style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
              onClick={() => setShowEventForm(!showEventForm)}
            >
              + Add Event
            </button>
            )}
          </div>

          {/* Event Association Form */}
          {showEventForm && (
            <form onSubmit={handleAssociateEvent} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Event</label>
                  <select style={styles.select} value={assocEventId} onChange={(e) => setAssocEventId(e.target.value)}>
                    <option value="">Select event...</option>
                    {events.map((ev) => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Sponsor Amount</label>
                  <input type="number" step="0.01" style={styles.input} value={assocAmount} onChange={(e) => setAssocAmount(e.target.value)} />
                </div>
                <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Associate</button>
              </div>
            </form>
          )}

          {/* Event Associations List */}
          {selectedDetail.events && selectedDetail.events.length > 0 ? (
            <table style={{ ...styles.table, fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Association Date</th>
                  <th style={styles.th}>Sponsor Amount</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedDetail.events.map((assoc) => (
                  <tr key={assoc.id}>
                    <td style={styles.td}>{assoc.event.name}</td>
                    <td style={styles.td}>{new Date(assoc.associationDate).toLocaleDateString()}</td>
                    <td style={styles.td}>{assoc.sponsorAmount != null ? formatCurrency(assoc.sponsorAmount) : '—'}</td>
                    <td style={styles.td}>
                      <button
                        style={{ ...styles.btnDanger, fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                        onClick={() => handleRemoveEventAssociation(assoc.eventId)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>No event associations.</p>
          )}
        </div>
      </div>
    );
  }
}
